/**
 * Connector pulls still being followed, kept in sessionStorage so a page
 * refresh can open their operation streams again. Entries are grouped under one
 * key per page and indexed by dataset id.
 *
 * Storage can be unavailable (private mode, quota), so every call falls back to
 * behaving as if nothing is stored rather than throwing.
 */

export const readRunningPulls = <T>(storageKey: string): Record<string, T> => {
  try {
    const stored = sessionStorage.getItem(storageKey);
    return stored ? (JSON.parse(stored) as Record<string, T>) : {};
  } catch {
    return {};
  }
};

const writeRunningPulls = <T>(
  storageKey: string,
  runningPulls: Record<string, T>
) => {
  try {
    if (Object.keys(runningPulls).length === 0) {
      sessionStorage.removeItem(storageKey);
    } else {
      sessionStorage.setItem(storageKey, JSON.stringify(runningPulls));
    }
  } catch {
    // Not stored: the pull still runs, it just cannot be resumed.
  }
};

export const saveRunningPull = <T>(
  storageKey: string,
  datasetId: string,
  entry: T
) =>
  writeRunningPulls(storageKey, {
    ...readRunningPulls<T>(storageKey),
    [datasetId]: entry
  });

export const removeRunningPull = (storageKey: string, datasetId: string) => {
  const { [datasetId]: _removed, ...rest } = readRunningPulls(storageKey);
  writeRunningPulls(storageKey, rest);
};
